import React, { useEffect, useState } from "react";
import { Box, Typography, Button, alpha, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import { components } from "../types/api";
import CampaignTable from "../components/table/CampaignTable";
import CampaignInternalCampaignsPanel from "../components/campaign/CampaignInternalCampaignsPanel";
import { useNavigate, useLocation } from "react-router-dom";
import { useGetUserDbCampaignsQuery } from "../store/apis/campaignApi";
import EmptyState from "../components/EmptyState";
import { useDispatch, useSelector } from "react-redux";
import { selectSidebarCollapsed } from "../store/slices/app/appSelector";
import { selectUserDb } from "../store/slices/session/sessionSelector";
import { setToastMessage } from "../store/slices/app/appSlice";
import PageBreadcrumbs from "../components/layout/PageBreadcrumbs";
import OrbLoader from "../components/common/OrbLoader";

// Use the newly extended API type
type Campaign = components["schemas"]["CampaignWithRelations"];

const Campaigns: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const collapsed = useSelector(selectSidebarCollapsed);
  const userDb = useSelector(selectUserDb);
  const isAdmin = userDb?.role === "ADMIN";
  const isClient = userDb?.role === "CLIENT";

  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(
    null
  );
  // Con datos en caché la tabla se pinta al instante y la petición se rehace en
  // segundo plano; el loader a pantalla completa solo sale la primera vez.
  const {
    data: userCampaignsResponse,
    isLoading: isLoadingUserCampaigns,
    isFetching: isFetchingUserCampaigns,
    error: userCampaignsError,
    refetch: refetchUserCampaigns,
  } = useGetUserDbCampaignsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [campaigns, setCampaigns] = useState<Campaign[]>(
    () => userCampaignsResponse?.data ?? []
  );
  // El overlay de "Actualizando…" solo para el refresco manual.
  const [isManualRefresh, setIsManualRefresh] = useState(false);

  useEffect(() => {
    if (userCampaignsError) {
      dispatch(
        setToastMessage({
          message: t("campaigns.userCampaignsError", {
            defaultValue: "Failed to load user campaigns",
          }),
          severity: "error",
        })
      );
    }
  }, [dispatch, t, userCampaignsError]);

  const handleRefreshAll = async () => {
    setIsManualRefresh(true);
    try {
      await refetchUserCampaigns();
    } finally {
      setIsManualRefresh(false);
    }
  };

  useEffect(() => {
    if (userCampaignsResponse?.data) {
      const campaignsData = userCampaignsResponse.data;
      setCampaigns(campaignsData);

      const locationState = location.state as
        | { selectedCampaignId?: string }
        | undefined;

      if (locationState?.selectedCampaignId) {
        const campaign = campaignsData.find(
          (c) => c.id === locationState.selectedCampaignId
        );

        if (campaign) {
          setSelectedCampaign(campaign);
        } else if (campaignsData.length > 0) {
          setSelectedCampaign(campaignsData[0]);
        }
        navigate(location.pathname, { replace: true, state: {} });
      } else {
        setSelectedCampaign((prev) => {
          if (prev && campaignsData.some((c) => c.id === prev.id)) {
            return prev;
          }
          return campaignsData.length > 0 ? campaignsData[0] : null;
        });
      }
    }
  }, [userCampaignsResponse, location.state, location.pathname, navigate]);

  const handleCampaignDeleted = (campaignId: string) => {
    setCampaigns((prevCampaigns) =>
      prevCampaigns.filter((campaign) => campaign.id !== campaignId)
    );
    if (selectedCampaign?.id === campaignId) {
      setSelectedCampaign(null);
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Box
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 4 },
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "80px" : "250px",
          display: "flex",
          flexDirection: "column",
          width: { xs: "calc(100% - 80px)", sm: "auto" },
          maxWidth: "100%",
          boxSizing: "border-box",
          minHeight: "100vh",
        }}
      >
        <PageBreadcrumbs />

        {isLoadingUserCampaigns ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100vh",
            }}
          >
            <OrbLoader
              size="xlarge"
              label={t("campaigns.loading", {
                defaultValue: "Cargando campañas…",
              })}
            />
          </Box>
        ) : !campaigns || campaigns.length === 0 ? (
          <EmptyState
            title={t("home.emptyStates.noCampaigns.title")}
            message={t("home.emptyStates.noCampaigns.message")}
            buttonText={t("home.emptyStates.noCampaigns.button")}
            buttonLink="/campaigns/new"
          />
        ) : (
          <>
            <Box sx={{ display: "flex", gap: 2, mb: 4, flexWrap: "wrap" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  py: 1.5,
                  px: 2.5,
                  borderRadius: 3,
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.background.paper,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    backgroundColor: theme.palette.info50.main,
                    color: theme.palette.info.main,
                  }}
                >
                  <CampaignOutlinedIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ lineHeight: 1.2 }}>
                    {campaigns.length || 0}
                  </Typography>
                  <Typography
                    variant="pSmall"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {t("campaigns.assignedLabel", {
                      defaultValue: "Asignadas",
                    })}
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  py: 1.5,
                  px: 2.5,
                  borderRadius: 3,
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.background.paper,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    backgroundColor: `${theme.palette.success.main}26`,
                    color: theme.palette.success.main,
                  }}
                >
                  <BoltOutlinedIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ lineHeight: 1.2 }}>
                    {campaigns.length || 0}
                  </Typography>
                  <Typography
                    variant="pSmall"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {t("campaigns.activeLabel", { defaultValue: "Activas" })}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ position: "relative" }}>
              {isManualRefresh && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 2,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    pt: 12,
                    borderRadius: 3,
                    backgroundColor: alpha(
                      theme.palette.background.default,
                      0.6
                    ),
                    backdropFilter: "blur(2px)",
                  }}
                >
                  <OrbLoader
                    size="large"
                    label={t("campaigns.refreshing", {
                      defaultValue: "Actualizando campañas…",
                    })}
                  />
                </Box>
              )}
              <CampaignTable
                campaignsData={campaigns}
                selectedCampaignId={selectedCampaign?.id}
                onSelectCampaign={setSelectedCampaign}
                onCampaignDeleted={handleCampaignDeleted}
                onOpenCampaign={(campaign) =>
                  navigate(`/campaigns/${campaign.id}`)
                }
                filtersStorageKey="beapolo_campaigns_filters"
                toolbarActions={
                  <>
                    <Button
                      variant="outlined"
                      onClick={handleRefreshAll}
                      disabled={isFetchingUserCampaigns}
                      startIcon={
                        isFetchingUserCampaigns ? (
                          <OrbLoader size="inline" />
                        ) : (
                          <RefreshIcon fontSize="small" />
                        )
                      }
                      sx={{ textTransform: "none", borderRadius: 6 }}
                    >
                      {t("common.refresh", { defaultValue: "Refresh" })}
                    </Button>
                    {isAdmin && (
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        sx={{
                          borderRadius: "50px",
                          textTransform: "none",
                          fontWeight: 700,
                        }}
                        onClick={() => navigate("/campaigns/new")}
                      >
                        {t("campaigns.new")}
                      </Button>
                    )}
                  </>
                }
                renderExpandedContent={(campaign) => (
                  <CampaignInternalCampaignsPanel
                    campaign={campaign}
                    isAdmin={isAdmin}
                    isClient={isClient}
                  />
                )}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Campaigns;
